import React, { createContext, useContext, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { SegmentedControl } from '@/components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { TASK_MANAGEMENT_NAV_ITEMS } from '../../components/adminNavConfig';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

// Tasks - one page, one menu item, with a view switcher (V2-036; DESIGN.md HDR-06, NAV-12).
//
// "Task Management" used to be a menu of four pages. Now Tasks is a direct link and the four
// lists are views of one page: All · Quantified · Routine · Project, each with its own URL and
// columns. Exactly one view is always selected; "All" is an option, not the absence of one. The
// view each person last used is remembered across sign-ins, so Tasks opens where they left it.
//
// The existing list pages are the views. Each reads TasksViewContext: inside this page its header
// title is "Tasks" and the switcher sits under it (PageHeader `viewToggle`).

export type TasksView = 'all' | 'quantified' | 'routine' | 'project';

export const TASKS_VIEW_PATHS: Record<TasksView, string> = {
  all: '/tasks/all',
  quantified: '/tasks/quantified',
  routine: '/tasks/routine',
  project: '/tasks/project',
};

const VIEW_LABELS: Record<TasksView, string> = {
  all: 'All',
  quantified: 'Quantified',
  routine: 'Routine',
  project: 'Project',
};

const ORDER: TasksView[] = ['all', 'quantified', 'routine', 'project'];

// Kept outside the `wrap.` prefix on purpose: that prefix is cleared at sign-out (IMP-07), and
// the Tasks view is a saved personal preference that must survive it (HDR-06). Per user.
const viewKey = (userId: number | string) => `prefs.tasksView:${userId}`;

export function rememberedTasksView(userId: number | string | undefined): TasksView | null {
  if (userId == null) return null;
  try {
    const v = window.localStorage.getItem(viewKey(userId));
    return v && (ORDER as string[]).includes(v) ? (v as TasksView) : null;
  } catch {
    return null;
  }
}

function rememberTasksView(userId: number | string | undefined, view: TasksView): void {
  if (userId == null) return;
  try {
    window.localStorage.setItem(viewKey(userId), view);
  } catch {
    // Storage blocked: the view still works, it just is not remembered.
  }
}

interface TasksViewContextValue {
  view: TasksView;
  /** The switcher, for the view's PageHeader `viewToggle`. */
  switcher: React.ReactNode;
}

const TasksViewContext = createContext<TasksViewContextValue | null>(null);

/** Inside the Tasks page: the current view and its switcher. Null on any other page. */
export const useTasksView = () => useContext(TasksViewContext);

function useAllowedViews(): TasksView[] {
  const { hasPermission } = usePermissions();
  return ORDER.filter((view) => {
    const item = TASK_MANAGEMENT_NAV_ITEMS.find((i) => i.to === TASKS_VIEW_PATHS[view]);
    return item ? item.visible(hasPermission) : false;
  });
}

/** /tasks – opens the view this person last used (or the first they can see). */
export const TasksIndexRedirect: React.FC = () => {
  const { currentUser } = useAuth();
  const allowed = useAllowedViews();
  const remembered = rememberedTasksView(currentUser?.id);
  const view = remembered && allowed.includes(remembered) ? remembered : allowed[0] ?? 'all';
  return <Navigate to={TASKS_VIEW_PATHS[view]} replace />;
};

export interface TasksPageProps {
  /** Renders the list for a view (the existing page, wrapped in its route guard). */
  renderView: (view: TasksView) => React.ReactNode;
}

const TasksPage: React.FC<TasksPageProps> = ({ renderView }) => {
  const { view: param } = useParams<{ view: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const allowed = useAllowedViews();
  const view = (ORDER as string[]).includes(param ?? '') ? (param as TasksView) : null;
  useDocumentTitle('Tasks');

  useEffect(() => {
    if (view && allowed.includes(view)) rememberTasksView(currentUser?.id, view);
  }, [view, allowed, currentUser?.id]);

  if (!view || !allowed.includes(view)) return <TasksIndexRedirect />;

  const switcher = (
    <SegmentedControl<TasksView>
      aria-label="Tasks view"
      value={view}
      onChange={(next) => navigate(TASKS_VIEW_PATHS[next])}
      options={allowed.map((v) => ({ value: v, label: VIEW_LABELS[v] }))}
    />
  );

  return (
    <TasksViewContext.Provider value={{ view, switcher }}>
      {/* Keyed by view: each view is its own list with its own columns and state. */}
      <React.Fragment key={view}>{renderView(view)}</React.Fragment>
    </TasksViewContext.Provider>
  );
};

export default TasksPage;
